import React from 'react';
import {
  CoreTheme,
  Div,
  Heading,
  Label,
  P,
  Button,
  Span,
} from '@kizi/core-components';
import styled from '@emotion/styled';
import {
  FieldHandler,
  useField,
  usePage,
  useWizardService,
} from '@kizi/comparator-core';

const Flex = styled(Div)`
  display: flex;
  flex-wrap: wrap;

  > div:not(:nth-child(1)) {
    margin-top: 5px;
  }

  ${({ theme }) => {
    const breakpoints = (theme as CoreTheme).breakpoints;
    return breakpoints && breakpoints.length > 0
      ? `
    @media (min-width: ${breakpoints[0]}) {
      > div:not(:nth-child(1)) {
        margin-top: 0;
      }
    }`
      : '';
  }}
`;

const FlexItem = styled(Div)`
  display: flex;
  min-width: 150px;
`;

export interface FieldDataHandlerProps {
  name: string;
  children: (field: FieldHandler) => React.ReactElement | null;
}

export const FieldDataHandler: React.FC<FieldDataHandlerProps> = ({
  name,
  children,
}) => {
  const service = useWizardService();
  if (!service) return null;
  const field = useField(name, service);
  if (!field || typeof children !== 'function') return null;
  return children(field);
};

type FieldTemplateProps = { name: string };
export const FieldTemplate: React.FC<FieldTemplateProps> = ({ name }) => {
  return (
    <FieldDataHandler name={name}>
      {({ active, value, meta, label }) => {
        if (active === false && value == 0) return null;
        return (
          <FlexItem
            p={[0, 0, '10px 0']}
            flex-direction={['column', 'column', 'row']}
            alignItems="center"
          >
            <Label display="flex" m={0} mr={1}>
              {label ? label : '-'}:
            </Label>
            <Div variant="footnoteRegular">
              {value} {meta?.unit ?? null}
            </Div>
          </FlexItem>
        );
      }}
    </FieldDataHandler>
  );
};

export interface UserDataSummaryProps {
  fields: string[];
  title: string;
}

export const UserDataSummaryWrapper: React.FC<UserDataSummaryProps> = ({
  title,
  fields,
  ...restProps
}) => {
  const { state } = usePage();
  const _fields = fields.map((field) => <FieldTemplate name={field} />);

  if (!state || (state.productList as any).length === 0) return null;

  const service = useWizardService();
  if (!service) return null;

  let onSubmitClick = () => {
    service?.send({ type: 'GOTO', index: '1' });
  };

  return (
    <Div {...restProps} display="flex" flexDirection="column">
      {title && (
        <Heading variant="H4" as="h4" textAlign="center" m={0}>
          {title}
        </Heading>
      )}
      <Flex
        flexDirection={['column', 'column', 'row']}
        justifyContent={[null, 'space-evenly']}
        alignContent={['center', '']}
        p={['10px 0', '10px 0', 0]}
      >
        {_fields}
      </Flex>
      <Div
        textAlign="center"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection={['column', 'row']}
      >
        <P variant="footnoteRegular" mr={1}>
          Kan ik mijn gegevens aanpassen?
        </P>
        <Button variant="transparentSmall" onClick={onSubmitClick}>
          Klik hier
        </Button>
      </Div>
      <P textAlign={'center'} mt={4} variant="bodyRegular">
        <Span variant="bodyRegular" fontWeight={1}>
          {'Let op: '}
        </Span>
        Energieaanbieders bepalen je definitieve maandbedrag op basis van het
        gemiddelde jaarverbruik in jouw woning in de afgelopen drie jaar. Dit
        kan afwijken van het verbruik dat jij hier zelf invult.
      </P>
    </Div>
  );
};
